import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, UserPlus, MessageCircle, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlayersPage = () => {
  const { token } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skillFilter, setSkillFilter] = useState('all');
  const [playTypeFilter, setPlayTypeFilter] = useState('all');
  const [inviting, setInviting] = useState(null);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const params = new URLSearchParams();
        if (skillFilter !== 'all') params.append('skill_level', skillFilter);
        if (playTypeFilter !== 'all') params.append('play_type', playTypeFilter);
        
        const response = await fetch(`${API}/players?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        
        if (response.ok) {
          setPlayers(await response.json());
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, [token, skillFilter, playTypeFilter]);

  const handleInvite = async (playerId) => {
    setInviting(playerId);
    try {
      const response = await fetch(`${API}/players/${playerId}/invite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Invitation sent!');
      } else {
        toast.error('Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setInviting(null);
    }
  };

  const getSkillBadgeClass = (skill) => {
    switch (skill?.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-blue-100 text-blue-800';
      case 'advanced': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="players-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Find Players</h1>
          <p className="text-muted-foreground">Connect with other players and arrange matches</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Select value={skillFilter} onValueChange={setSkillFilter}>
            <SelectTrigger className="w-[180px]" data-testid="skill-filter">
              <SelectValue placeholder="All Skill Levels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Skill Levels</SelectItem>
              <SelectItem value="Beginner">Beginner</SelectItem>
              <SelectItem value="Intermediate">Intermediate</SelectItem>
              <SelectItem value="Advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>

          <Select value={playTypeFilter} onValueChange={setPlayTypeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="playtype-filter">
              <SelectValue placeholder="All Play Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Play Types</SelectItem>
              <SelectItem value="Singles">Singles</SelectItem>
              <SelectItem value="Doubles">Doubles</SelectItem>
              <SelectItem value="Open Play">Open Play</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Players Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-xl"></div>)}
          </div>
        ) : players.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {players.map((player) => (
              <Card key={player.user_id} className="card-hover" data-testid={`player-${player.user_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start space-x-4">
                    <Avatar className="h-14 w-14">
                      <AvatarImage src={player.picture} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                        {player.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold">{player.name}</h3>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge className={getSkillBadgeClass(player.skill_level)}>
                          {player.skill_level}
                        </Badge>
                        <Badge variant="outline">{player.play_type}</Badge>
                      </div>
                      {player.bio && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{player.bio}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => handleInvite(player.user_id)}
                      disabled={inviting === player.user_id}
                      className="flex-1 btn-primary"
                      data-testid={`invite-${player.user_id}`}
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      {inviting === player.user_id ? 'Sending...' : 'Invite to Play'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2">No players found</h3>
            <p className="text-muted-foreground">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlayersPage;
